import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useHubspotTracker } from '~components/business-owner/marketing/hooks/useHubspotTracker';

import { MarketingAttributionEvents } from '~constants/fullstory/events/marketing-attribution';
import { HubspotEvents } from '~constants/hubspot/events';
import { GlobalState } from '~reducers';
import { SessionState } from '~reducers/session';
import { trackEvent } from '~third-party/fullstory';

interface TrackingEvents {
	hubspot: HubspotEvents;
	fullstory: string;
}

/**
 * Maps tracked URL pathnames to their HubSpot and FullStory events.
 * Both systems deduplicate once per session per URL (sessionStorage keyed by userId + event name).
 * URL path keys cannot be camelCase, so naming-convention lint is disabled for this block.
 */
/* eslint-disable @typescript-eslint/naming-convention */
const URL_EVENT_MAP: Partial<Record<string, TrackingEvents>> = {
	'/dashboard/dispatch': {
		hubspot: HubspotEvents.FS_PUD_DISPATCH_DASHBOARD,
		fullstory: MarketingAttributionEvents.FS_PUD_DISPATCH_DASHBOARD,
	},
	'/dashboard/customers/commercial-customers': {
		hubspot: HubspotEvents.FS_PUD_COMMERCIAL_CUSTOMERS,
		fullstory: MarketingAttributionEvents.FS_PUD_COMMERCIAL_CUSTOMERS,
	},
	'/dashboard/orders/commercial': {
		hubspot: HubspotEvents.FS_PUD_COMMERCIAL_ORDERS,
		fullstory: MarketingAttributionEvents.FS_PUD_COMMERCIAL_ORDERS,
	},
	'/messenger': {
		hubspot: HubspotEvents.FS_ASSIST_MESSENGER_ROOT,
		fullstory: MarketingAttributionEvents.FS_ASSIST_MESSENGER_ROOT,
	},
	'/messenger/inbox': {
		hubspot: HubspotEvents.FS_ASSIST_MESSENGER_INBOX,
		fullstory: MarketingAttributionEvents.FS_ASSIST_MESSENGER_INBOX,
	},
	'/messenger/call-center': {
		hubspot: HubspotEvents.FS_ASSIST_CALL_CENTER,
		fullstory: MarketingAttributionEvents.FS_ASSIST_CALL_CENTER,
	},
	'/messenger/call-center/calls': {
		hubspot: HubspotEvents.FS_ASSIST_CALL_CENTER_CALLS,
		fullstory: MarketingAttributionEvents.FS_ASSIST_CALL_CENTER_CALLS,
	},
	'/messenger/assist-settings': {
		hubspot: HubspotEvents.FS_ASSIST_SETTINGS,
		fullstory: MarketingAttributionEvents.FS_ASSIST_SETTINGS,
	},
	'/messenger/notifications': {
		hubspot: HubspotEvents.FS_ASSIST_MESSENGER_NOTIFICATIONS,
		fullstory: MarketingAttributionEvents.FS_ASSIST_MESSENGER_NOTIFICATIONS,
	},
	'/dashboard/marketing': {
		hubspot: HubspotEvents.FS_ACCELERATE_MARKETING_ROOT,
		fullstory: MarketingAttributionEvents.FS_ACCELERATE_MARKETING_ROOT,
	},
	'/dashboard/marketing/blasts': {
		hubspot: HubspotEvents.FS_ACCELERATE_MARKETING_BLASTS,
		fullstory: MarketingAttributionEvents.FS_ACCELERATE_MARKETING_BLASTS,
	},
	'/global-settings/promotions/promotions': {
		hubspot: HubspotEvents.FS_ACCELERATE_PROMOTIONS_SETTINGS,
		fullstory: MarketingAttributionEvents.FS_ACCELERATE_PROMOTIONS_SETTINGS,
	},
	'/global-settings/task-manager': {
		hubspot: HubspotEvents.FS_ACCELERATE_TASK_MANAGER,
		fullstory: MarketingAttributionEvents.FS_ACCELERATE_TASK_MANAGER,
	},
	'/global-settings/promotions/referral-program': {
		hubspot: HubspotEvents.FS_ACCELERATE_REFERRAL_PROGRAM,
		fullstory: MarketingAttributionEvents.FS_ACCELERATE_REFERRAL_PROGRAM,
	},
	'/global-settings/promotions/loyalty-program': {
		hubspot: HubspotEvents.FS_ACCELERATE_LOYALTY_PROGRAM,
		fullstory: MarketingAttributionEvents.FS_ACCELERATE_LOYALTY_PROGRAM,
	},
};
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Fires page-visit events to HubSpot and FullStory whenever the user navigates to a tracked route.
 * Both events are deduplicated once per session per URL (sessionStorage keyed by userId + event name).
 * Renders nothing — mount once inside BusinessOwnerRoutes to cover all authenticated pages.
 */
export function PageVisitTracker(): null {
	const { pathname } = useLocation();
	const { trackHubspotEvent } = useHubspotTracker();

	// selfServeAssist is needed for specific routes that are only visible when the feature flag is enabled. If the flag is off, the page is invisible even if the URL matches, so we skip tracking in that case.
	const { selfServeAssist: isSelfServeAssistEnabled } = useFlags<{
		selfServeAssist: boolean;
	}>();
	const { userId, email, firstName, lastName, business } = useSelector(
		(state: GlobalState) => state.session as SessionState,
	);
	const isAssistEnabled = useSelector(
		(state: GlobalState) =>
			(
				state.businessOwner.globalSettings.accountSettings as {
					settings?: { isAssistEnabled?: boolean };
				}
			).settings?.isAssistEnabled,
	);

	useEffect(() => {
		const events = URL_EVENT_MAP[pathname];
		if (!events) {
			return;
		}

		if (
			pathname === '/messenger/assist-settings' &&
			(!isSelfServeAssistEnabled || !isAssistEnabled)
		) {
			return;
		}

		// FullStory: deduplicated once per session per URL
		const fsSessionKey = `fsTracked_${userId}_${events.fullstory}`;
		if (!sessionStorage.getItem(fsSessionKey)) {
			sessionStorage.setItem(fsSessionKey, '1');
			trackEvent(events.fullstory, {
				userId,
				customerEmail: email,
				customerName: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
				businessId: business?.id,
				businessName: business?.name,
			});
		}

		// HubSpot: deduplicated once per session per URL
		const hsSessionKey = `hsTracked_${userId}_${events.hubspot}`;
		if (!sessionStorage.getItem(hsSessionKey)) {
			sessionStorage.setItem(hsSessionKey, '1');
			trackHubspotEvent(events.hubspot);
		}
	}, [
		pathname,
		userId,
		email,
		firstName,
		lastName,
		business,
		trackHubspotEvent,
		isSelfServeAssistEnabled,
		isAssistEnabled,
	]);

	return null;
}
